import { inject, Injectable } from '@angular/core';
import { HttpClientCustom } from '../custom/http-client';

import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Util } from 'src/shared/util/util';
import { ProsQueriesService } from 'src/shared/services/pros-queries.service';

@Injectable()
export class Pros {
  private readonly prosQueries = inject(ProsQueriesService);

  constructor(
    private httpClient: HttpClientCustom,
    private storage: TidyStorage,
    private onboardingProvider: OnboardingProvider,
    private util: Util
  ) {}

  async updateMaxJobs(payload: { team_id: number, key: string, value: number }) {
    const url = 'api/v1/customer/team-settings';
    return this.httpClient.put(url, payload);
  }

  getBlacklistedPros() {
    // Delegate to TanStack Query service for caching
    return this.prosQueries.getBlacklistedPros();
  }

  async blacklistPro(proIds: number[]) {
    const url = `api/v1/customer/blacklisted-homekeepers`;
    const response = await this.httpClient.post(url, { homekeeper_ids: proIds });
    // Invalidate cache after blacklisting pro
    this.prosQueries.invalidateAllPros();
    this.prosQueries.invalidateBlacklistedPros();
    return response;
  }

  async unblacklistPro(proId: number) {
    const url = `/api/v1/customer/blacklisted-homekeepers/${proId}/inactivate`;
    const response = await this.httpClient.post(url, {});
    // Invalidate cache after unblacklisting pro
    this.prosQueries.invalidateAllPros();
    this.prosQueries.invalidateBlacklistedPros();
    return response;
  }

  async getAllPros() {
    const hasProperties = await this.onboardingProvider.checkIfHasProperties();
    if (!hasProperties) {
      return [];
    }
    // Delegate to TanStack Query service for caching
    return this.prosQueries.getAllPros();
  }

  async getAllProsByAddress(addressId?: number) {
    const id = addressId || parseInt(localStorage.getItem('addressId'));
    // Delegate to TanStack Query service for caching
    return this.prosQueries.getAllProsByAddress(id);
  }

  async getAllPrivateProsByAddress(addressId?) {
    // Delegate to TanStack Query service for caching
    return this.prosQueries.getAllPrivateProsByAddress(addressId);
  }

  async getAllPrivatePros() {
    // Delegate to TanStack Query service for caching
    return this.prosQueries.getAllPrivatePros();
  }

  async checkIfHasPrivatePro(proListResponse = null) {
    // Delegate to TanStack Query service for caching
    return this.prosQueries.checkIfHasPrivatePro(proListResponse);
  }

  async getAllNoPrivatePros() {
    // Delegate to TanStack Query service for caching
    return this.prosQueries.getAllNoPrivatePros();
  }

  async checkIfHasNoPrivatePros() {
    // Delegate to TanStack Query service for caching
    return this.prosQueries.checkIfHasNoPrivatePros();
  }

  async addNewPro(params) {
    const url = `api/v1/customer/homekeepers`;
    const response = await this.httpClient.post(url, params);
    localStorage.setItem('hasPrivatePro', 'true');
    // Invalidate cache after adding a new pro
    this.prosQueries.invalidateAllPros();
    return response;
  }

  async updateProInfo(proId: number, profile: Partial<{name: string, email: string, phone: string}>) {
    const url = `api/v1/customer/homekeepers/${proId}`;
    const data = {homekeeper_id: proId, profile};
    const response = await this.httpClient.put(url, data);
    // Invalidate cache after updating pro info
    this.prosQueries.invalidateAllPros();
    return response;
  }

  async addExistingPros(params, addressId) {
    const url = `api/v1/customer/addresses/${addressId}/homekeeper-list/add-homekeepers`;
    const response = await this.httpClient.post(url, params);
    // Invalidate cache after adding existing pros
    this.prosQueries.invalidateAllPros();
    return response;
  }

  getProsExcludingAddress() {
    const addressId = parseInt(localStorage.getItem('addressId'));
    const url = `api/v1/customer/homekeepers?exclude_homeekepers_on_address_id=${addressId}`;
    return this.httpClient.get(url);
  }

  getPros(serviceType: number, addressId = null) {
    const id = addressId || parseInt(localStorage.getItem('addressId'));
    const formattedServiceType = serviceType.toString();
    const url = `api/v1/customer/addresses/${id}/homekeeper-list?service_type_id=${formattedServiceType}`;
    return this.httpClient.get(url);
  }

  async getServiceCategories() {
    const serviceTypes = await this.storage.retrieve('serviceTypes');
    if (serviceTypes) {
      return serviceTypes;
    } else {
      const url = 'api/v1/customer/service-types';
      const response = this.httpClient.get(url);
      await this.storage.save('serviceTypes', response);
      return response;
    }
  }

  getProDetail(proId, addressId = null) {
    // `?? parseInt(null)` yields NaN when the key is absent (?? only guards
    // null/undefined), and NaN passes through into `addresses/NaN` in the URL.
    // Fall back to '0' like pros-queries.service so a missing key 404s cleanly.
    const id = addressId ?? parseInt(localStorage.getItem('addressId') || '0');
    const url = `api/v1/customer/homekeepers/${proId}/addresses/${id}/details-page`;
    return this.httpClient.get(url);
  }

  async updateProsPriorities(pros, serviceTypeId: number) {
    const addressId = parseInt(localStorage.getItem('addressId'));
    const url = `api/v1/customer/addresses/${addressId}/homekeeper-list`;
    const params = {
      service_type_id: serviceTypeId,
      ...pros
    };
    params.approved = this.filterApprovedGroup(params.approved, params.favorited);
    const response = await this.httpClient.put(url, params);
    // Invalidate cache after updating priorities
    this.prosQueries.invalidateAllPros();
    return response;
  }

  filterApprovedGroup(approvedPriorities, favoritedPriorities) {
    return approvedPriorities.filter(approvedPriority => {
      const isHomekeeper = approvedPriority.object_type === 'homekeeper';
      if (!isHomekeeper) {
        return true;
      }

      const isFavorited = favoritedPriorities?.some(favoritedPriority => favoritedPriority.id === approvedPriority.id);
      if (isFavorited) {
        return false;
      }

      return true;
    });
  }

  getAllProsSettings(perPage, page, params) {
    let url = `api/v1/customer/team-address-services?filters[only_private_homekeepers]=true&per_page=${perPage}&page=${page}`;
    if (params?.homekeeper_ids) {
      url += `&filters[homekeeper_ids]=${params.homekeeper_ids.join(',')}`;
    }
    if (params?.service_category_ids) {
      url += `&filters[service_category_ids]=${params.service_category_ids.join(',')}`;
    }
    if (params?.address_ids) {
      url += `&filters[address_ids]=${params.address_ids.join(',')}`;
    }
    return this.httpClient.getFullRequest(url)
      .then(response => ({
          body: response.body,
          totalRecords: response.headers.get('X-Total-Records')
        }));
  }

  async batchUpdateProSettings(payload) {
    const url = 'api/v1/customer/team-address-services/batch-update';
    const response = await this.httpClient.post(url, payload);
    // Invalidate cache after batch updating pro settings
    this.prosQueries.invalidateAllPros();
    return response;
  }

  getAutopaySettingsAllPros() {
    const url = `api/v1/customer/team-settings`;
    return this.httpClient.get(url);
  }

  getAutopaySettings(teamId) {
    const url = `api/v1/customer/team-settings?team_id=${teamId}`;
    return this.httpClient.get(url);
  }

  updateAutopaySettings(payload) {
    const url = 'api/v1/customer/team-settings';
    return this.httpClient.put(url, payload);
  }

  sendPayment(payload, homekeeperId) {
    const url = `api/v1/customer/homekeepers/${homekeeperId}/send-payment`;
    return this.httpClient.post(url, payload);
  }

  getCreditBalance(homekeeperTeamId, addressId = null) {
    let url = `api/v1/customer/billing/credit-balance?homekeeper_team_id=${homekeeperTeamId}`;
    if (addressId) {
      url += `&address_id=${addressId}`
    }
    return this.httpClient.get(url);
  }

  getBillingHistory(proId) {
    const url = `api/v1/customer/billing/private-detail-history?homekeeper_id=${proId}`;
    return this.httpClient.get(url);
  }

  getBillingHistoryByAddress(proId, addressId) {
    const url = `api/v1/customer/billing/private-detail-history?homekeeper_id=${proId}&addresses[]=${addressId}`;
    return this.httpClient.get(url);
  }

  async checkIfHasProsInCategory(pros) {
    let hasPros = false;
    pros.approved.map((pro) => {
      if (pro.object_type !== 'dynamic_sa') {
        hasPros = true;
      }
    });
    pros.favorited.map((pro) => {
      if (pro.object_type !== 'dynamic_sa') {
        hasPros = true;
      }
    });
    return hasPros;
  }

  parseProsIntoGroups(prosData) {
    const allPros = [];

    const greatestPriority = prosData.favorited.reduce((max, pro) => {
      if (Array.isArray(pro)) {
        pro.forEach((item) => Math.max(max, item.priority));
      } else {
        return Math.max(max, pro.priority);
      }
    }, 0);

    prosData.favorited.forEach((pro) => {
      allPros.push(pro);
    });
    prosData.approved.forEach((pro) => {
      pro.priority = greatestPriority + 1;
      pro.has_priority = true;
      allPros.push(pro);
    });
    const pros = [];
    allPros.map((pro, index) => {
      if (index !== 0) {
        const priorPro = pros[pros?.length - 1];
        const priorProIsArray = Array.isArray(priorPro);
        const priorPriority = priorProIsArray ? priorPro[0].priority : priorPro.priority;
        if (priorPriority === pro.priority) {
          if (priorProIsArray) {
            pros[pros?.length - 1].push(pro);
          } else {
            pros[pros?.length - 1] = [
              priorPro,
              pro
            ];
          }
        } else {
          pros.push(pro);
        }
      } else {
        pros.push(pro);
      }
    });
    prosData.favorited = pros;
    return prosData;
  }

  updatePriorityListSentence(prosData) {
    const notBlockedPros = [...prosData?.favorited, ...prosData?.approved];
    if (!notBlockedPros.length) {
      return new TranslationPipe().transform('Pros that you\'ve added or had jobs with will appear here.');
    }
    let favoritedSentence = new TranslationPipe().transform('TIDY will send job requests to ');

    // Don't sort - show pros in their actual priority order
    // Auto-assign doesn't change the order, it just means they get auto-assigned without needing to accept
    const favoritedList = prosData?.favorited || [];

    favoritedList.map((pro, index) => {
      let proName = '';
      const isArray = Array.isArray(pro);
      if (isArray) {
        pro.map((item, itemIndex) => {
          const name = this.isNotDynamicSA(item) ? item?.object?.first_name : new TranslationPipe().transform('new pros');
          const isAutoAssign = this.hasAutoAssignPro(item);
          const styledName = isAutoAssign ? `<strong class="auto-assign-pro">${name}</strong>` : `<strong>${name}</strong>`;
          proName += styledName + (itemIndex < pro.length - 1 ? new TranslationPipe().transform(' and ') : '');
        });
      } else {
        const name = this.isNotDynamicSA(pro) ? pro?.object?.first_name : new TranslationPipe().transform('new pros');
        const isAutoAssign = this.hasAutoAssignPro(pro);
        proName = isAutoAssign ? `<strong class="auto-assign-pro">${name}</strong>` : `<strong>${name}</strong>`;
      }
      favoritedSentence += proName + (index < favoritedList.length - 1 ? new TranslationPipe().transform(', then ') : '');
    });
    const hasOnlyFindNewPros =
      prosData?.favorited?.length === 0 && prosData?.approved?.length === 1 && !this.isNotDynamicSA(prosData?.approved[0]) ||
      prosData?.favorited?.length === 1 && prosData?.approved?.length === 0 && !this.isNotDynamicSA(prosData?.favorited[0]);
    if (hasOnlyFindNewPros) {
      favoritedSentence = new TranslationPipe().transform('TIDY will send job requests to <strong>new pros</strong>.')
    } else {
      favoritedSentence += '.';
    }
    return favoritedSentence;
  }

  isNotDynamicSA(item) {
    return item?.object_type !== 'dynamic_sa';
  }

  hasAutoAssignPro(pro) {
    if (!pro) {return false;}

    if (Array.isArray(pro)) {
      return pro.some(item => item?.ignore_homekeeper_availability === true);
    }

    return pro?.ignore_homekeeper_availability === true;
  }

  async removeProFromAddressList(addressId, payload) {
    const url = `api/v1/customer/addresses/${addressId}/homekeeper-list/inactivate-homekeepers`;
    const response = await this.httpClient.put(url, payload);
    // Invalidate cache after removing pro from address
    this.prosQueries.invalidateAllPros();
    return response;
  }

  saveServiceCategories(categories: any[]): void {
    this.storage.save('serviceCategories', categories);
    const date = new Date();
    this.storage.save('lastServiceCategoriesUpdate', date.getTime());
  }

  async getServiceCategoriesFromStorage(): Promise<any[]> {
    return await this.storage.retrieve('serviceCategories');
  }

  async getServiceCategoriesOrFetch(): Promise<any> {
    const date = new Date();
    const lastServiceCategoriesUpdate = await this.storage.retrieve('lastServiceCategoriesUpdate');
    const hasLessThenOneHourOfDifference = (date.getTime() - lastServiceCategoriesUpdate) / 1000 / 60 / 60 < 1;
    const storedServiceCategories = await this.getServiceCategoriesFromStorage();
    if (lastServiceCategoriesUpdate && storedServiceCategories.length > 0 && hasLessThenOneHourOfDifference) {
      return storedServiceCategories;
    }
    const serviceCategories = await this.getServiceCategories();
    this.saveServiceCategories(serviceCategories);
    return serviceCategories;
  }

  parseDurationIntoHoursAndMintes(duration, _type) {
    if (!duration) {
      return null;
    }
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    let hoursAndMinutes = '';
    if (hours > 0) {
      hoursAndMinutes += hours + (hours === 1 ? new TranslationPipe().transform(' hour ') : new TranslationPipe().transform(' hours '));
    }
    if (minutes > 0) {
      hoursAndMinutes += minutes + new TranslationPipe().transform(' minutes');
    }
    return hoursAndMinutes;
  }

  async updateAutoAssignSettings(payload, addressHomekeeperPriorityID) {
    const url = `api/v1/customer/address-homekeeper-priorities/${addressHomekeeperPriorityID}`;
    const response = await this.httpClient.put(url, payload);
    // Invalidate cache after updating auto-assign settings
    this.prosQueries.invalidateAllPros();
    return response;
  }

  async setFindNewPros(payload) {
    const url = 'api/v1/customer/address-homekeeper-priorities/set-dynamic-sa';
    const response = await this.httpClient.post(url, payload);
    // Invalidate cache after setting find new pros
    this.prosQueries.invalidateAllPros();
    return response;
  }

  async createManyPros(payload) {
    const url = 'api/v1/customer/homekeepers/create-many';
    const response = await this.httpClient.post(url, payload);
    // Invalidate cache after creating many pros
    this.prosQueries.invalidateAllPros();
    return response;
  }

  async moveProToCollection(selectedAddressId, selectedCategory, findNewPro) {
    localStorage.setItem('addressId', selectedAddressId);
    let prosData = await this.getPros(selectedCategory);
    prosData = this.parseProsIntoGroups(prosData);
    prosData.blocked = prosData.blocked.flat();
    prosData.approved = prosData.approved.flat();
    prosData.favorited = prosData.favorited.flat().filter(pro => pro.priority);
    const approvedIndex = prosData.approved.findIndex(pro => pro.object_type === 'dynamic_sa');
    const favoritedIndex = prosData.favorited.findIndex(pro => pro.object_type === 'dynamic_sa');
    const blockedIndex = prosData.blocked.findIndex(pro => pro.object_type === 'dynamic_sa');
    let removedElement: any[];
    if (approvedIndex >= 0) {
      removedElement = prosData.approved.splice(approvedIndex, 1);
    }
    if (favoritedIndex >= 0) {
      removedElement = prosData.favorited.splice(favoritedIndex, 1);
    }
    if (blockedIndex >= 0) {
      removedElement = prosData.blocked.splice(blockedIndex, 1);
    }
    if (removedElement) {
      const indexToAdd = 0;
      if (findNewPro) {
        prosData.approved.splice(indexToAdd, 0, ...removedElement);
      } else {
        prosData.blocked.splice(indexToAdd, 0, ...removedElement);
      }
    }
    try {
      const serviceTypeId = 1;
      await this.updateProsPriorities(prosData, serviceTypeId);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  getSendPaymentPreview(homekeeperId, payload) {
    const url = `api/v1/customer/homekeepers/${homekeeperId}/send-payment-preview`;
    return this.httpClient.post(url, payload);
  }
  
}
